import L from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet/dist/leaflet.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  GeoJSON,
  MapContainer,
  Marker,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import CompareSlider from "./CompareSlider"; // ✨ IMPORT KOMPONEN BARU DI SINI
import Timeline from "./components/Timeline";
import "./Project.css";
import { Pannellum } from "pannellum-react"; // Komenkan ini, asumsikan ada 360 viewer

const API_BASE = (process.env.REACT_APP_API_BASE || "/api").replace(/\/+$/, "");

// ICON BARU UNTUK FOTO INDIVIDUAL
const makePhotoIcon = (photo) => {
  const thumbUrl = `${API_BASE}${photo.file_url}`.replace(
    "/uploads/",
    "/uploads/.thumbnails/"
  );
  // HTML sekarang memiliki div thumbnail dan div untuk "ekor" pin
  const html = `
    <div class="photo-marker-pin">
      <div class="photo-marker-thumbnail" style="background-image: url(${thumbUrl})"></div>
      <div class="photo-marker-tip"></div>
    </div>`;

  return L.divIcon({
    html,
    className: "photo-icon-container", // className ini penting agar background transparan
    iconSize: [70, 82], // Ukuran diperbesar untuk mengakomodasi "ekor"
    iconAnchor: [35, 82], // Anchor sekarang di ujung bawah tengah "ekor"
  });
};

// GANTI FUNGSI LAMA DENGAN VERSI BARU YANG LEBIH CERDAS INI
const createClusterIcon = (cluster) => {
  const childMarkers = cluster.getAllChildMarkers();
  const photoCount = cluster.getChildCount();

  // 1. Hitung jumlah proyek dan folder unik di dalam cluster
  const uniqueProjectNames = new Set(
    childMarkers.map((marker) => marker.options.projectName)
  );
  const projectCount = uniqueProjectNames.size;

  const uniqueFolderNames = new Set(
    childMarkers.map((marker) => marker.options.folderName)
  );
  const folderCount = uniqueFolderNames.size;

  const esc = (s) =>
    String(s).replace(
      /[<>&]/g,
      (m) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[m])
    );

  // KONDISI 2: CLUSTER SINGLE-PROYEK, MULTI-FOLDER
  const firstMarker = childMarkers[0];
  const { projectName, projectColor } = firstMarker.options;

  if (folderCount > 1) {
    const html = `
      <div class="multi-location-pin" style="color: ${projectColor || "#ef4444"
      };">
        <div class="pin-badge" style="background-color: ${projectColor || "#ef4444"
      }; border-color: ${projectColor || "#ef4444"};">
          ${folderCount}
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
        </svg>
        <div class="project-label-on-pin">${esc(projectName)}</div>
      </div>`;

    return L.divIcon({
      html: html,
      className: "multi-location-pin-container",
      iconSize: [60, 60],
      iconAnchor: [30, 48],
    });
  }

  // KONDISI 3: CLUSTER SINGLE-FOLDER (Logika lama Anda, sudah bagus)
  const { folderName } = firstMarker.options;
  const html = `
    <div class="cam-marker" style="border-color: ${projectColor || "#0ea5b0"}">
      <div class="cam-icon" style="background-color: ${projectColor || "#0ea5b0"
    }">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M4 7h3l1-2h8l1 2h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zm8 3a4 4 0 1 0 .001 8.001A4 4 0 0 0 12 10zm0 2a2 2 0 1 1-.001 4.001A2 2 0 0 1 12 12z"/>
        </svg>
      </div>
      <div class="photo-count">${photoCount}</div>
      <div class="cam-label">${esc(folderName)}</div>
    </div>`;

  return L.divIcon({
    html,
    className: "cam-marker-container",
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  });
};

/* ---------- helpers ---------- */
const toNum = (v) => (Number.isFinite(+v) ? +v : null);
const hasLL = (r) => toNum(r?.lat) != null && toNum(r?.lon) != null;
const clampNum = (value, min, max) => Math.min(max, Math.max(min, value));
const pctToPanoramaYaw = (xPct) =>
  clampNum(((Number(xPct) || 0) / 100) * 360 - 180, -180, 180);
const pctToPanoramaPitch = (yPct) =>
  clampNum(90 - ((Number(yPct) || 0) / 100) * 180, -85, 85);

const renderPanoramaAnnotationTooltip = (hotSpotDiv, args = {}) => {
  if (hotSpotDiv.dataset.annotationReady === "true") return;

  hotSpotDiv.dataset.annotationReady = "true";
  hotSpotDiv.setAttribute(
    "aria-label",
    args.text ? `Annotation: ${args.text}` : "Annotation"
  );
  hotSpotDiv.setAttribute("title", args.text || "Annotation");

  const marker = document.createElement("div");
  marker.className = "panorama-annotation-hotspot";
  marker.style.setProperty("--annotation-color", args.color || "#22c55e");

  const bubble = document.createElement("div");
  bubble.className = "panorama-annotation-bubble";
  bubble.textContent = args.text || "No description";

  marker.appendChild(bubble);
  hotSpotDiv.appendChild(marker);
};

const getDayDate = (d) => {
  if (!d) return null;
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
};

const avg = (arr) =>
  arr.length ? arr.reduce((s, n) => s + n, 0) / arr.length : null;
const pickDate = (r) =>
  r?.taken_at ||
  r?.captured_at ||
  r?.created_at ||
  r?.updated_at ||
  r?.uploaded_at ||
  null;

function makeCamIcon(label = "", color = "#0ea5b0", count = 0) {
  const esc = (s) =>
    String(s).replace(
      /[<>&]/g,
      (m) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[m])
    );

  const html = `
    <div class="cam-marker" style="border-color: ${color}">
      <div class="cam-icon" style="background-color: ${color}">
        <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
          <path d="M4 7h3l1-2h8l1 2h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zm8 3a4 4 0 1 0 .001 8.001A4 4 0 0 0 12 10zm0 2a2 2 0 1 1-.001 4.001A2 2 0 0 1 12 12z"/>
        </svg>
      </div>
      ${count > 0 ? `<div class="photo-count">${count}</div>` : ""}
      <div class="cam-label">${esc(label)}</div>
    </div>`;

  return L.divIcon({
    html,
    className: "cam-marker-container",
    iconSize: [40, 40],
    iconAnchor: [20, 40],
  });
}

/* ---------- Fullscreen Viewer ---------- */
// ✨ TAMBAHKAN PROPS `mode` DENGAN NILAI DEFAULT 'photo'
function ViewerModal({
  open,
  onClose,
  folder,
  projectName,
  photos,
  startIndex = 0,
  apiBase,
  onAnnotationSaved,
  mode = "photo",
}) {
  // ---- state ----
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const [annotate, setAnnotate] = useState(false);
  const [panelOpen, setPanelOpen] = useState(true);
  const imgBoxRef = useRef(null);
  const [editCache, setEditCache] = useState({});
  const [hoverId, setHoverId] = useState(null);

  // notes: { [photoKey]: Array<{id, dbId?, x, y, color, text, editing, saving?}> }
  const [notes, setNotes] = useState({});

  const [annMetaMap, setAnnMetaMap] = useState({});
  const unique = (arr) => [...new Set((arr || []).filter(Boolean))];
  // Compare mode
  const [compare, setCompare] = useState(false);
  const [leftIdx, setLeftIdx] = useState(0);
  const [rightIdx, setRightIdx] = useState(0);
  const [split, setSplit] = useState(50); // 0..100 %

  const [draggingNoteId, setDraggingNoteId] = useState(null); // ID anotasi yang sedang diseret
  const isPanorama = mode === "panorama";
  const showPanorama360 = isPanorama && !annotate && !compare;
  const allowAnnotationEditing = !compare && (!isPanorama || annotate);
  const showAnnotationPanel = !compare;
  const showAnnotationActions = allowAnnotationEditing && annotate;
  const openPanoramaAnnotationPanel = useCallback(() => {
    setPanelOpen(true);
  }, []);

  // ---- DB helpers / identity ----
  const ANN_ENDPOINT = `${String(apiBase).replace(
    /\/+$/,
    ""
  )}/photo_annotation`;

  // Menghitung posisi kursor menjadi persentase di dalam canvas
  const getPctFromClient = useCallback((clientX, clientY) => {
    if (!imgBoxRef.current) return { x: 0, y: 0 };
    const box = imgBoxRef.current.getBoundingClientRect();
    const x = Math.min(
      100,
      Math.max(0, ((clientX - box.left) / box.width) * 100)
    );
    const y = Math.min(
      100,
      Math.max(0, ((clientY - box.top) / box.height) * 100)
    );
    return { x, y };
  }, []); // Handler Pointer Down (Mulai Drag)

  const onNotePointerDown = (e, noteId) => {
    e.stopPropagation(); // Hanya izinkan drag jika mode annotate aktif DAN sedang dalam mode editing
    const note = (notes[photoKey] || []).find((n) => n.id === noteId);
    // ✨ PERBAIKAN: HANYA IZINKAN DRAG PADA MODE 'photo'
    if (!allowAnnotationEditing || !annotate || !note?.editing) return;

    setDraggingNoteId(noteId); // Ambil fokus/capture kursor untuk memastikan drag berlanjut di luar elemen
    e.target.setPointerCapture(e.pointerId);
  }; // Efek untuk menangani perpindahan kursor secara global

  useEffect(() => {
    if (!draggingNoteId) return;

    const onPointerMove = (e) => {
      if (e.pointerId === undefined) return;
      const { x, y } = getPctFromClient(e.clientX, e.clientY);

      setPhotoNotes(photoKey, (arr) =>
        arr.map((n) => (n.id === draggingNoteId ? { ...n, x, y } : n))
      );
    };

    const onPointerUp = (e) => {
      if (e.pointerId === undefined) return; // Setelah drop, persistensi dilakukan saat user klik 'Save' di editor
      setDraggingNoteId(null); // Pastikan event target melepaskan capture
      try {
        e.target.releasePointerCapture(e.pointerId);
      } catch (err) {
        /* ignore */
      }
    };

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerUp);

    return () => {
      document.removeEventListener("pointermove", onPointerMove);
      document.removeEventListener("pointerup", onPointerUp);
      document.removeEventListener("pointercancel", onPointerUp);
    };
  }, [draggingNoteId, getPctFromClient]); // Dependensi yang diperlukan

  // Safer identity: try localStorage keys; fall back to JWT claims
  function decodeJwtSafe(token) {
    try {
      const base64 = token.split(".")[1];
      if (!base64) return null;
      const norm = base64
        .replace(/-/g, "+")
        .replace(/_/g, "/")
        .padEnd(Math.ceil(base64.length / 4) * 4, "=");
      return JSON.parse(atob(norm));
    } catch {
      return null;
    }
  }
  function lsGet(...keys) {
    for (const k of keys) {
      try {
        const v = localStorage.getItem(k);
        if (v && v !== "null" && v !== "undefined") return v;
      } catch { }
    }
    return "";
  }

  const token = localStorage.getItem("token") || "";
  const decoded = token ? decodeJwtSafe(token) : null;

  const currentEmail = lsGet("email") || decoded?.email || "";

  const currentOrgLS =
    lsGet(
      "organization",
      "organization_name",
      "org",
      "department",
      "dept",
      "dept_name"
    ) ||
    decoded?.organization ||
    "";

  // normalize file_url (store relative path; server usually indexes on relative)
  function stripApiBaseFromUrl(u) {
    if (!u || typeof u !== "string") return u;
    const base = String(apiBase).replace(/\/+$/, "");
    return u.startsWith(base) ? u.slice(base.length) || "/" : u;
  }

  // clamp % to 0..100
  const pctNum = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
  };

  // ---- helpers ----
  const photoKeyOf = (x) =>
    x?.id ?? x?.photo_id ?? x?.file_url ?? x?.photo_name ?? x?.name ?? null;

  const fmtDate = (d) =>
    d
      ? d.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "";

  const fmtDateTime = (d) =>
    d
      ? d.toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
      : "";

  const getDate = (r) => {
    const raw =
      r?.taken_at || // <-- Prioritas pertama: ambil dari data EXIF
      r?.captured_at ||
      r?.created_at ||
      r?.uploaded_at ||
      r?.updated_at ||
      null;
    const dt = raw ? new Date(raw) : null;
    return Number.isFinite(dt?.getTime?.()) ? dt : null;
  };

  function getFolderName(r) {
    if (!r) return null;
    const s = (v) => (v && typeof v === "string" ? v : null);
    const fromPath = (p) => {
      if (typeof p !== "string") return null;
      const clean = p.split("?")[0].split("#")[0];
      const parts = clean.split("/").filter(Boolean);
      return parts.length >= 2 ? parts[parts.length - 2] : null;
    };
    return (
      s(r.photo_folder) ??
      s(r.folder) ??
      s(r.folder_name) ??
      s(r.album) ??
      s(r.subfolder) ??
      fromPath(r.folder_path) ??
      fromPath(r.file_url) ??
      null
    );
  }

  function getProjectNameFrom(r) {
    if (!r) return null;
    const s = (v) => (v && typeof v === "string" ? v : null);
    const fromObj =
      s(r.project_name) ??
      s(r.projectName) ??
      s(r.project_title) ??
      s(r.projectTitle) ??
      s(r.project?.name) ??
      s(r.meta?.project_name);

    if (fromObj) return fromObj;

    const fromPath = (p) => {
      if (typeof p !== "string") return null;
      const clean = p.split("?")[0].split("#")[0];
      const parts = clean.split("/").filter(Boolean);
      const idx =
        parts.lastIndexOf("projects") !== -1
          ? parts.lastIndexOf("projects")
          : parts.lastIndexOf("project");
      if (idx !== -1 && parts[idx + 1]) {
        const slug = parts[idx + 1];
        const title = slug
          .replace(/[-_]+/g, " ")
          .replace(/\s+/g, " ")
          .trim()
          .replace(/\b\w/g, (m) => m.toUpperCase());
        return title || null;
      }
      return null;
    };

    return fromPath(r.project_path) ?? fromPath(r.file_url) ?? null;
  }

  // Compute the sorted position of startIndex (prevents flicker)
  function computePos(list, rawIndex) {
    if (!list?.length) return 0;
    const target = list[rawIndex] ?? list[0];
    const key = photoKeyOf(target);

    const withIdx = list
      .map((p, i) => ({
        p,
        i,
        t: getDate(p)?.getTime() ?? Number.POSITIVE_INFINITY,
      }))
      .sort((a, b) => a.t - b.t || a.i - b.i);

    if (key == null) return Math.min(rawIndex || 0, withIdx.length - 1);
    const pos = withIdx.findIndex((x) => photoKeyOf(x.p) === key);
    return pos >= 0 ? pos : 0;
  }

  // Initialize idx from sorted position
  useEffect(() => {
    if (!open) return;
    setIdx(computePos(photos, startIndex));
  }, [open, startIndex, photos]);

  // Sort photos earliest -> latest
  const sortedPhotos = useMemo(() => {
    // Buat salinan array photos agar tidak memodifikasi state secara langsung
    const photosCopy = [...(photos || [])];

    // Urutkan berdasarkan tanggal, jika tanggal tidak ada, gunakan indeks asli
    photosCopy.sort((a, b) => {
      const dateA = getDate(a)?.getTime() ?? Number.POSITIVE_INFINITY;
      const dateB = getDate(b)?.getTime() ?? Number.POSITIVE_INFINITY;

      // Urutkan berdasarkan waktu, jika waktu sama, jaga urutan aslinya
      if (dateA !== dateB) {
        return dateA - dateB;
      }

      // Fallback to stable sort if dates are equal (using original index is a good idea)
      return photos.indexOf(a) - photos.indexOf(b);
    });

    return photosCopy;
  }, [photos]);

  // 2) NOW it's safe to reference sortedPhotos inside this effect
  useEffect(() => {
    if (!open || !sortedPhotos?.length) return;
    let cancelled = false;
    const CONCURRENCY = 4;
    const MAX_COLORS = 8;
    let i = 0,
      inFlight = 0;

    const kick = () => {
      while (inFlight < CONCURRENCY && i < sortedPhotos.length) {
        const idxLocal = i++;
        const sp = sortedPhotos[idxLocal];
        const fileUrl = sp?.file_url;
        if (!fileUrl || annMetaMap[idxLocal]) continue;

        inFlight++;
        (async () => {
          try {
            const rows = await fetchPhotoAnnotations(fileUrl);
            if (cancelled) return;
            const colors = unique(rows.map((r) => r.color || "#22c55e")).slice(
              0,
              MAX_COLORS
            );
            setAnnMetaMap((m) =>
              m[idxLocal]
                ? m
                : { ...m, [idxLocal]: { count: rows.length, colors } }
            );
          } catch {
            if (cancelled) return;
            setAnnMetaMap((m) =>
              m[idxLocal] ? m : { ...m, [idxLocal]: { count: 0, colors: [] } }
            );
          } finally {
            inFlight--;
            if (!cancelled) kick();
          }
        })();
      }
    };

    kick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sortedPhotos]);

  // Timeline data
  // Timeline data
  const timeline = useMemo(
    () =>
      sortedPhotos.map((p, i) => ({
        // i sekarang adalah indeks di array yang sudah diurutkan
        i,
        // t adalah timestamp dari foto
        t: getDate(p)?.getTime() ?? Number.POSITIVE_INFINITY,
      })),
    [sortedPhotos]
  );
  const tMin = useMemo(
    () => (timeline.length ? Math.min(...timeline.map((x) => x.t)) : 0),
    [timeline]
  );
  const tMax = useMemo(
    () => (timeline.length ? Math.max(...timeline.map((x) => x.t)) : 0),
    [timeline]
  );

  // Annotation metadata (counts + colors) per photo for timeline UI
  const annMeta = useMemo(
    () =>
      sortedPhotos.map((p, i) => {
        const fromPrefetch = annMetaMap[i];
        if (fromPrefetch) return fromPrefetch;

        const arr = notes[photoKeyOf(p)] || [];
        return {
          count: arr.length,
          colors: unique(arr.map((n) => n.color)).slice(0, 8),
        };
      }),
    [sortedPhotos, annMetaMap, notes]
  );

  // Keyboard & body scroll lock (disable arrow nav in compare)
  useEffect(() => {
    // ✨ JANGAN HILANGKAN NAVIGASI DENGAN PANAH PADA MODE PANORAMA
    if (!open || compare) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight")
        setIdx((i) => Math.min(i + 1, Math.max(sortedPhotos.length - 1, 0)));
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(i - 1, 0));
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, sortedPhotos.length, compare]);

  // Reset zoom on image change
  useEffect(() => setZoom(1), [idx]);

  useEffect(() => {
    if (sortedPhotos.length < 2 && compare) {
      setCompare(false);
    }
  }, [sortedPhotos.length, compare]);

  // Seed compare selection when opening or changing idx
  useEffect(() => {
    if (!open) return;
    setLeftIdx(idx);
    setRightIdx(Math.max(0, idx - 1));
  }, [open, idx]);

  // ====== DB I/O ======
  async function fetchPhotoAnnotations(file_url) {
    try {
      const res = await fetch(
        `${ANN_ENDPOINT}?file_url=${encodeURIComponent(file_url)}`,
        { headers: { Accept: "application/json" } }
      );
      if (!res.ok)
        throw new Error(`GET failed ${res.status} ${await res.text()}`);
      const rows = await res.json();
      return Array.isArray(rows) ? rows : [];
    } catch (err) {
      console.error("Fetch annotations failed:", err);
      return [];
    }
  }

  async function createAnnotation(row) {
    try {
      const res = await fetch(ANN_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(row),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`POST failed ${res.status} ${text}`);
      }
      const created = await res.json().catch(() => ({}));
      return created; // expect at least { id: ... }
    } catch (err) {
      console.error("Create annotation failed:", err);
      throw err;
    }
  }

  async function updateAnnotation(id, patch) {
    try {
      const res = await fetch(`${ANN_ENDPOINT}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(patch),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`PUT failed ${res.status} ${text}`);
      }
      const updated = await res.json().catch(() => ({}));
      return updated;
    } catch (err) {
      console.error("Update annotation failed:", err);
      throw err;
    }
  }

  async function removeAnnotation(id) {
    try {
      const res = await fetch(`${ANN_ENDPOINT}/${id}`, { method: "DELETE" });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`DELETE failed ${res.status} ${text}`);
      }
      return true;
    } catch (err) {
      console.error("Delete annotation failed:", err);
      throw err;
    }
  }

  // Load annotations whenever active photo changes
  useEffect(() => {
    if (!open) return;
    const rec = sortedPhotos[idx] || {};
    const file_url = rec?.file_url; // should already be relative (e.g., /uploads/..)
    const key = photoKeyOf(rec) ?? "noid";
    if (!file_url) {
      setNotes((m) => ({ ...m, [key]: [] }));
      return;
    }
    (async () => {
      const rows = await fetchPhotoAnnotations(file_url);
      const mapped = rows.map((r) => ({
        id: `db:${r.id}`, // stable UI id
        dbId: r.id,
        x: Number(r.x_pct) || 0,
        y: Number(r.ypct ?? r.y_pct) || 0, // accept either column on read
        color: r.color || "#22c55e",
        text: r.annotation_text || "",
        editing: false,
      }));
      setNotes((m) => ({ ...m, [key]: mapped }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, idx, sortedPhotos]);

  // ---- computed for current photo ----
  if (!open) return null;

  const p = sortedPhotos[idx] || {};
  const orgForSave = currentOrgLS || (p?.organization ?? "");
  const currentFolder = getFolderName(p) ?? folder;
  const currentProject = getProjectNameFrom(p) ?? projectName;

  // derive a file name for DB (prefer explicit name; else take from URL)
  const fileName =
    p?.photo_name ||
    p?.name ||
    (typeof p?.file_url === "string"
      ? p.file_url.split("?")[0].split("#")[0].split("/").pop() || null
      : null);

  const photoKey = photoKeyOf(p) ?? "noid";
  const photoNotes = notes[photoKey] || [];
  const panelNotes = photoNotes.filter(
    (n) => n.dbId != null || n.editing || n.saving
  );
  const panoramaHotspots = photoNotes.filter(
    (n) =>
      (n.dbId != null || n.saving) &&
      Number.isFinite(Number(n.x)) &&
      Number.isFinite(Number(n.y))
  );
  const savedAnnotationCount = photoNotes.filter((n) => n.dbId != null).length;
  const when = getDate(p);
  const dateStr = fmtDate(when);
  const dateTimeStr = fmtDateTime(when);

  const showLeft = idx > 0 && !compare;
  const showRight = idx < sortedPhotos.length - 1 && !compare;

  const setPhotoNotes = (photoId, updater) =>
    setNotes((m) => ({ ...m, [photoId]: updater(m[photoId] || []) }));

  const openEditor = (noteId) => {
    // ✨ PERBAIKAN: JANGAN IZINKAN BUKA EDITOR PADA MODE PANORAMA
    if (!allowAnnotationEditing) return;
    setEditCache((m) => {
      const found = (notes[photoKey] || []).find((x) => x.id === noteId);
      return found
        ? {
          ...m,
          [noteId]: {
            text: found.text,
            color: found.color,
            x: found.x,
            y: found.y,
          },
        }
        : m;
    });
    setPhotoNotes(photoKey, (arr) =>
      arr.map((x) => (x.id === noteId ? { ...x, editing: true } : x))
    );
  };

  const deleteNote = async (noteId) => {
    // ✨ PERBAIKAN: JANGAN IZINKAN DELETE PADA MODE PANORAMA
    if (!allowAnnotationEditing) return;
    // optimistic UI
    const note = (notes[photoKey] || []).find((n) => n.id === noteId);
    setPhotoNotes(photoKey, (arr) => arr.filter((x) => x.id !== noteId));
    setEditCache((m) => {
      const { [noteId]: _drop, ...rest } = m;
      return rest;
    });
    if (hoverId === noteId) setHoverId(null);

    // delete from DB if persisted
    try {
      if (note?.dbId != null) {
        await removeAnnotation(note.dbId);
        onAnnotationSaved(); // <--- BARU: Panggil callback setelah delete
      }
    } catch (err) {
      console.error("Delete note (server) failed:", err);
      // optional: revert UI on failure
    }
  };

  const addNoteAt = (e) => {
    // ✨ PERBAIKAN: JANGAN IZINKAN TAMBAH ANNOTATION PADA MODE PANORAMA
    if (!allowAnnotationEditing || !annotate || !imgBoxRef.current) return;

    // --- PERBAIKAN 1: Hapus anotasi baru yang belum disimpan SEBELUM membuat yang baru ---
    setPhotoNotes(photoKey, (arr) => {
      const unsavedNewNote = arr.find((x) => x.editing && x.dbId == null);
      if (unsavedNewNote) {
        setEditCache((m) => {
          const { [unsavedNewNote.id]: _drop, ...rest } = m;
          return rest;
        }); // Hapus dari array notes jika belum tersimpan di DB
        return arr.filter((x) => x.id !== unsavedNewNote.id);
      }
      return arr;
    }); // ---------------------------------------------------------------------------------
    const box = imgBoxRef.current.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * 100;
    const y = ((e.clientY - box.top) / box.height) * 100;
    const n = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      x,
      y,
      color: "#22c55e",
      text: "",
      editing: true,
      saving: false,
      dbId: null,
    };
    setPhotoNotes(photoKey, (arr) => [n, ...arr]);
    setEditCache((m) => ({ ...m, [n.id]: { text: n.text, color: n.color } }));
  };

  // Persist a note to DB (create or update)
  async function persistNote(note) {
    // ✨ PERBAIKAN: JANGAN IZINKAN PERSIST PADA MODE PANORAMA
    if (!allowAnnotationEditing) return;

    // Normalize to relative path before saving
    const rawFileUrl = stripApiBaseFromUrl(p?.file_url || "");

    const baseRow = {
      project_name: currentProject || null,
      organization: orgForSave || null,
      email: currentEmail || null,
      file_name: fileName || null,
      file_url: rawFileUrl || null, // prefer relative path in DB
      annotation_text: note.text || "",
      x_pct: pctNum(note.x),
      y_pct: pctNum(note.y),
      color: note.color || "#22c55e",
    };

    // optimistic saving flag
    setPhotoNotes(photoKey, (arr) =>
      arr.map((x) => (x.id === note.id ? { ...x, saving: true } : x))
    );

    try {
      if (note.dbId == null) {
        // CREATE
        const created = await createAnnotation(baseRow);
        const newDbId = created?.id ?? created?.[0]?.id ?? null;
        setPhotoNotes(photoKey, (arr) =>
          arr.map((x) =>
            x.id === note.id
              ? { ...x, dbId: newDbId, editing: false, saving: false }
              : x
          )
        );
      } else {
        // UPDATE (text/color; add x_pct/ypct here if you implement drag-to-move)
        await updateAnnotation(note.dbId, {
          annotation_text: baseRow.annotation_text,
          color: baseRow.color,
          x_pct: baseRow.x_pct, // <-- TAMBAHKAN INI
          y_pct: baseRow.y_pct, // <-- TAMBAHKAN INI
        });
        setPhotoNotes(photoKey, (arr) =>
          arr.map((x) =>
            x.id === note.id ? { ...x, editing: false, saving: false } : x
          )
        );
      }
      // clear edit snapshot
      setEditCache((m) => {
        const { [note.id]: _, ...rest } = m;
        return rest;
      });
      onAnnotationSaved(); // <-- Panggil callback setelah berhasil disimpan
    } catch (err) {
      console.error("Persist note failed:", err);
      // turn off saving flag, keep editing open
      setPhotoNotes(photoKey, (arr) =>
        arr.map((x) => (x.id === note.id ? { ...x, saving: false } : x))
      );
      alert("Failed to save annotation to server. Check console for details.");
    }
  }

  // Fungsi pembantu untuk membatalkan pengeditan/menghapus anotasi baru
  const cancelNote = (noteId) => {
    // ✨ PERBAIKAN: JANGAN IZINKAN CANCEL PADA MODE PANORAMA
    if (!allowAnnotationEditing) return;

    const note = (notes[photoKey] || []).find((n) => n.id === noteId);
    if (!note) return;

    if (note.dbId == null) {
      // HAPUS: Jika ini anotasi baru (belum punya ID DB)
      setPhotoNotes(photoKey, (arr) => arr.filter((x) => x.id !== noteId));
    } else {
      // KEMBALIKAN: Jika ini anotasi yang sudah ada
      const snap = editCache[noteId];
      setPhotoNotes(photoKey, (arr) =>
        arr.map((x) =>
          x.id === noteId
            ? {
              ...x,
              editing: false,
              ...(snap
                ? {
                  text: snap.text,
                  color: snap.color,
                  x: snap.x,
                  y: snap.y,
                }
                : {}),
            }
            : x
        )
      );
    }
    setEditCache((m) => {
      const { [noteId]: _, ...rest } = m;
      return rest;
    });
  };

  // Labels for timeline tooltips (kept)
  const firstLabel = fmtDate(getDate(sortedPhotos[0]));
  const currentLabel = fmtDate(getDate(sortedPhotos[idx]));
  const lastLabel = fmtDate(getDate(sortedPhotos[sortedPhotos.length - 1]));

  return createPortal(
    <div className="fv-modal" role="dialog" aria-modal="true">
      {/* HEADER */}
      <div className="fv-head">
        <div className="fv-title">
          <div className="name">
            {getFolderName(sortedPhotos[idx]) ?? folder}
          </div>
          <div className="sub">
            {getProjectNameFrom(sortedPhotos[idx]) ?? projectName}
            {dateStr ? ` - ${dateStr}` : ""}
          </div>
        </div>

        <div className="fv-actions">
          {/* Shared compare support for both image and panorama files */}
          <button
            className={`btn ${compare ? "primary" : ""}`}
            onClick={() => setCompare((v) => !v)}
            aria-pressed={compare}
            disabled={sortedPhotos.length < 2}
            title={
              sortedPhotos.length < 2
                ? "Need at least two files to compare"
                : compare
                  ? "Exit compare mode"
                  : "Compare two images"
            }
          >
            <i className="bi bi-view-stacked" />{" "}
            {compare ? "Comparing" : "Compare"}
          </button>
          {/* Shared annotation support for both image and panorama files */}
          <button
            className={`btn ${annotate ? "primary" : ""}`}
            onClick={() => setAnnotate((v) => !v)}
            disabled={compare}
            title={
              compare
                ? "Disable compare to annotate"
                : isPanorama
                  ? annotate
                    ? "Return to 360 panorama view"
                    : "Switch to flat panorama mode for annotation"
                  : "Toggle annotate"
            }
          >
            <i className="bi bi-image" /> Annotate
          </button>
          <button className="btn icon" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" />
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className={`fv-body ${mode === "panorama" ? "panorama-mode" : ""}`}>
        <div className={`fv-stage ${panelOpen ? "with-panel" : ""}`}>
          {/* Annotation panel stays available for panorama read-only viewing */}
          {showAnnotationPanel && (
            <button
              className="panel-toggle"
              onClick={() => setPanelOpen((v) => !v)}
              title={panelOpen ? "Hide annotations" : "Show annotations"}
            >
              <i className="bi bi-journal-text" />
            </button>
          )}

          {/* ✨ TOMBOL NAVIGASI KIRI/KANAN TETAP ADA */}
          {!compare && (
            <>
              <button
                className="nav-btn left"
                onClick={() => setIdx((i) => Math.max(0, i - 1))}
                disabled={!(idx > 0)}
                aria-label="Previous photo"
              >
                <i className="bi bi-chevron-left" />
              </button>
              <button
                className="nav-btn right"
                onClick={() =>
                  setIdx((i) =>
                    Math.min(i + 1, Math.max(sortedPhotos.length - 1, 0))
                  )
                }
                disabled={!(idx < sortedPhotos.length - 1)}
                aria-label="Next photo"
              >
                <i className="bi bi-chevron-right" />
              </button>
            </>
          )}

          {/* Stage content */}
          {compare ? (
            <div className="canvas compare">
              {/* ✨ GANTI DENGAN KOMPONEN BARU DI SINI */}
              <CompareSlider
                sortedPhotos={sortedPhotos}
                leftIdx={leftIdx}
                rightIdx={rightIdx}
                split={split}
                setLeftIdx={setLeftIdx}
                setRightIdx={setRightIdx}
                setSplit={setSplit}
                apiBase={API_BASE}
                notes={notes}
                setNotes={setNotes}
              />
            </div>
          ) : (
            <>
              {/* Panorama defaults to 360 view, but annotate/compare use the flat image canvas */}
              {showPanorama360 ? (
                // Default immersive 360 panorama view
                <div
                  ref={imgBoxRef}
                  className="canvas panorama-canvas"
                >
                  {p?.file_url ? (
                    <div style={{ width: '100%', height: '100%' }}>
                      <Pannellum
                        width="100%"
                        height="100%"
                        image={`${apiBase}${p.file_url}`}
                        pitch={10}
                        yaw={180}
                        hfov={110}
                        autoLoad
                        showZoomCtrl={true}
                        mouseZoom={true}
                        showFullscreenCtrl={true}
                        onLoad={() => {
                          console.log("Panorama loaded successfully");
                        }}
                        onError={(err) => {
                          console.error("Pannellum error:", err);
                        }}
                      >
                        {panoramaHotspots.map((n) => (
                          <Pannellum.Hotspot
                            key={n.id}
                            type="custom"
                            pitch={pctToPanoramaPitch(n.y)}
                            yaw={pctToPanoramaYaw(n.x)}
                            cssClass="panorama-annotation-anchor"
                            tooltip={renderPanoramaAnnotationTooltip}
                            tooltipArg={{
                              text: n.text || "No description",
                              color: n.color || "#22c55e",
                            }}
                            handleClick={openPanoramaAnnotationPanel}
                            handleClickArg={{ noteId: n.id }}
                          />
                        ))}
                      </Pannellum>
                    </div>
                  ) : (
                    <div className="empty">No panorama image available</div>
                  )}
                  <div className="panorama-overlay">
                    <p>
                      <strong>{p.photo_name || "Unnamed"}</strong> <br />
                      360 Panorama View
                      {savedAnnotationCount > 0
                        ? ` - ${savedAnnotationCount} annotation${savedAnnotationCount > 1 ? "s" : ""
                        }`
                        : ""}
                    </p>
                  </div>
                </div>
              ) : (
                // Shared flat image canvas for regular photos and panorama annotate mode
                <div
                  ref={imgBoxRef}
                  className="canvas"
                  onClick={addNoteAt}
                  style={{ cursor: annotate ? "crosshair" : "default" }}
                >
                  {p?.file_url ? (
                    <img
                      src={`${apiBase}${p.file_url}`}
                      alt={p.photo_name || getFolderName(p) || folder}
                      style={{ transform: `scale(${zoom})` }}
                      draggable={false}
                    />
                  ) : (
                    <div className="empty">No photo</div>
                  )}

                  {isPanorama && (
                    <div className="panorama-overlay">
                      <p>
                        <strong>{p.photo_name || "Unnamed"}</strong> <br />
                        {annotate
                          ? "Flat Panorama Annotation Mode"
                          : "Flat Panorama Image View"}
                      </p>
                    </div>
                  )}

                  {/* notes & editors */}
                  {(notes[photoKey] || []).map((n) => {
                    const isLow = n.y > 70;

                    return (
                      <div
                        key={n.id}
                        className="note"
                        style={{
                          left: `${n.x}%`,
                          top: `${n.y}%`,
                          background: n.color,
                          opacity: n.saving ? 0.6 : 1,
                          cursor:
                            n.editing && draggingNoteId === n.id
                              ? "grabbing"
                              : n.editing
                                ? "grab"
                                : "default", // Kursor default saat tidak edit/drag
                          pointerEvents: "auto",
                        }}
                        onPointerDown={(e) => onNotePointerDown(e, n.id)} // <-- TAMBAHKAN HANDLER DRAG
                        onMouseEnter={() => setHoverId(n.id)}
                        onMouseLeave={() =>
                          setHoverId((h) => (h === n.id ? null : h))
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          // Hanya buka editor jika annotation aktif & tidak sedang diseret
                          if (annotate && !n.editing && !draggingNoteId)
                            openEditor(n.id);
                        }}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          if (annotate) openEditor(n.id); // <-- TAMBAHKAN annotate
                        }}
                        tabIndex={annotate ? 0 : -1} // <-- NONAKTIFKAN TAB INDEX JIKA TIDAK BISA DIEDIT
                        onKeyDown={(e) => {
                          if (!annotate) return; // <-- TAMBAHKAN CEK ANNOTATE
                          if (e.key === "Enter") openEditor(n.id);
                          if (e.key === "Delete") deleteNote(n.id);
                        }}
                        aria-label={
                          n.text ? `Annotation: ${n.text}` : "Annotation"
                        }
                        title="Click or double-click to edit"
                      >
                        {!n.editing && !!n.text && (
                          <div className="note-pop">{n.text}</div>
                        )}
                        {/* TOOLBAR HANYA MUNCUL JIKA annotate aktif */}
                        {!n.editing &&
                          hoverId === n.id &&
                          annotate && ( // <-- TAMBAHKAN annotate
                            <div
                              className="note-toolbar"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                className="icon"
                                title="Edit"
                                onClick={() => openEditor(n.id)}
                              >
                                <i className="bi bi-pencil" />
                              </button>
                              <button
                                className="icon danger"
                                title="Delete"
                                onClick={() => deleteNote(n.id)}
                                disabled={n.saving}
                              >
                                <i className="bi bi-trash" />
                              </button>
                            </div>
                          )}
                        {n.editing && (
                          <div
                            className="note-editor"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              top: isLow ? "auto" : "16px", // <<-- Perbaikan posisi vertikal
                              bottom: isLow ? "16px" : "auto", // <<-- Perbaikan posisi vertikal
                              transformOrigin: isLow
                                ? "bottom left"
                                : "top left",
                            }}
                          >
                            <textarea
                              placeholder="Type annotation…"
                              value={n.text}
                              onChange={(e) =>
                                setPhotoNotes(photoKey, (arr) =>
                                  arr.map((x) =>
                                    x.id === n.id
                                      ? { ...x, text: e.target.value }
                                      : x
                                  )
                                )
                              }
                            />
                            <div className="row">
                              <div className="swatch-group">
                                {["#ef4444", "#f59e0b", "#22c55e", "#2dd4bf"].map(
                                  (c) => (
                                    <button
                                      key={c}
                                      type="button"
                                      className={`dot${n.color === c ? " active" : ""
                                        }`}
                                      style={{
                                        "--swatch": c,
                                        backgroundColor: c,
                                      }}
                                      title="Change color"
                                      onClick={() =>
                                        setPhotoNotes(photoKey, (arr) =>
                                          arr.map((x) =>
                                            x.id === n.id
                                              ? { ...x, color: c }
                                              : x
                                          )
                                        )
                                      }
                                    />
                                  )
                                )}
                              </div>
                              <span className="spacer" />
                              <button
                                className="tiny danger"
                                title="Delete"
                                type="button"
                                onClick={() => deleteNote(n.id)}
                                disabled={n.saving}
                              >
                                Delete
                              </button>
                              <button
                                className="tiny"
                                title="Cancel"
                                type="button"
                                onClick={() => cancelNote(n.id)}
                                disabled={n.saving}
                              >
                                Cancel
                              </button>
                              <button
                                className="primary"
                                type="button"
                                onClick={() => persistNote(n)}
                                disabled={n.saving}
                              >
                                {n.dbId == null ? "Save" : "Save changes"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ✨ TIMELINE HANYA UNTUK MODE PHOTO BIASA */}
              <div className="timeline">
                <div className="current-time">{dateStr || "—"}</div>
                <div className="track">
                  <div className="rail" />
                  {timeline.map((t) => {
                    const pct =
                      tMin === tMax
                        ? (t.i / Math.max(sortedPhotos.length - 1, 1)) * 100
                        : ((t.t - tMin) / (tMax - tMin)) * 100;

                    const active = t.i === idx;

                    const tipDate =
                      fmtDate(getDate(sortedPhotos[t.i])) || `#${t.i + 1}`;
                    const tipFolder = getFolderName(sortedPhotos[t.i]);
                    const tipProject = getProjectNameFrom(sortedPhotos[t.i]);

                    const count = annMeta[t.i]?.count || 0;

                    const tickStyle = { left: `${pct}%` };

                    return (
                      <button
                        key={t.i}
                        className={`tick${active ? " active" : ""}`}
                        style={tickStyle}
                        title={[tipDate, tipFolder, tipProject]
                          .filter(Boolean)
                          .join(" – ")}
                        onClick={() => setIdx(t.i)}
                        aria-label={`${tipDate}${count
                            ? `, ${count} annotation${count > 1 ? "s" : ""}`
                            : ""
                          }`}
                      />
                    );
                  })}
                </div>

                <div className="tick-labels">
                  {timeline.map((t) => {
                    const pct =
                      tMin === tMax
                        ? (t.i / Math.max(sortedPhotos.length - 1, 1)) * 100
                        : ((t.t - tMin) / (tMax - tMin)) * 100;

                    const d = fmtDate(getDate(sortedPhotos[t.i])) || "";
                    const colors = annMeta[t.i]?.colors || [];
                    const shown = colors.slice(0, 5);
                    const more = Math.max(0, colors.length - shown.length);

                    return (
                      <div
                        key={`lab-${t.i}`}
                        className={`tick-label${t.i === idx ? " active" : ""
                          }`}
                        style={{ left: `${pct}%` }}
                      >
                        <div className="date">{d}</div>
                        <div
                          className="ann-group"
                          aria-hidden={colors.length === 0}
                        >
                          {shown.map((c, k) => (
                            <span
                              key={k}
                              className="ann-dot"
                              style={{ background: c }}
                            />
                          ))}
                          {more > 0 && (
                            <span className="ann-more">+{more}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Annotation panel */}
        {showAnnotationPanel && (
          <aside className={`side ${panelOpen ? "open" : "closed"}`}>
            <div className="side-head">
              <h4>Annotations</h4>
            </div>
            <div className="side-list">
              {panelNotes.length === 0 ? (
                <div className="side-empty">
                  {isPanorama && !annotate ? (
                    <>
                      No annotations for this panorama. Switch to <b>Annotate</b>{" "}
                      to add one.
                    </>
                  ) : (
                    <>
                      No annotations for this photo. Click <b>Annotate</b> to add
                      one.
                    </>
                  )}
                </div>
              ) : (
                panelNotes.map((n) => (
                    <div
                      key={n.id}
                      className="card"
                      style={{ opacity: n.saving ? 0.6 : 1 }}
                    >
                      <span className="dot" style={{ background: n.color }} />
                      <div className="body">
                        {n.text || <span className="muted">No description</span>}
                      </div>
                      <div className="actions">
                        {showAnnotationActions && (
                          <>
                            <button
                              className="edit"
                              title="Edit"
                              onClick={() => openEditor(n.id)}
                              disabled={n.saving || !annotate}
                            >
                              <i className="bi bi-pencil" />
                            </button>
                            <button
                              className="delete"
                              title="Delete"
                              onClick={() => deleteNote(n.id)}
                              disabled={n.saving || !annotate}
                            >
                              <i className="bi bi-trash" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </aside>
        )}
      </div>

      <style>{`
        .fv-modal{
          position:fixed; inset:0; background:#0b1324; color:#e9f1fb;
          display:flex; flex-direction:column; z-index:2147483600;
        }
        .fv-head{
          display:flex; align-items:center; gap:12px;
          background:#0f1627; border-bottom:1px solid #ffffff1f;
          padding:10px 14px;
        }
        .fv-title{flex:1;}
        .fv-title .name{font-size:20px; font-weight:800;}
        .fv-title .sub{font-size:13px; color:#a9b8cf; margin-top:2px;}
        .fv-actions{display:flex; gap:8px;}
        .btn{background:#22324f; color:#e9f1fb; border:1px solid #31476f; border-radius:10px; padding:8px 10px; cursor:pointer;}
        .btn.icon{padding:8px 10px}
        .btn.primary{background:#2b4c7e; border-color:#3e67a6;}
        .btn:disabled{opacity:.6; cursor:not-allowed;}

        .fv-body{
          position:relative; flex:1;
          display:grid; grid-template-columns: minmax(0,1fr) 360px;
          align-items:stretch; gap:0; min-height:0;
        }
        /* ✨ Hapus class panorama-mode, biar layoutnya sama */
        /* Akhir dari CSS baru */

        .fv-stage{
          position:relative; display:flex; flex-direction:column;
          background:#0f1627; border-right:1px solid #ffffff1a;
        }
        .fv-stage.with-panel{border-right:none;}

        .panel-toggle{
          position:absolute; right:8px; top:8px; z-index:15;
          width:34px; height:34px; border-radius:999px; border:1px solid #2a3f65;
          background:#15213a; color:#e9f1fb; display:grid; place-items:center; cursor:pointer;
        }

        .nav-btn{
          position:absolute; top:50%; transform:translateY(-50%);
          width:44px; height:44px; border-radius:999px; border:1px solid #243657;
          background:#121b2e; color:#e9f1fb; display:grid; place-items:center;
          box-shadow:0 6px 18px rgba(0,0,0,.35); z-index:14; opacity:.95;
        }
        .nav-btn:disabled{opacity:.35; cursor:not-allowed;}
        .nav-btn.left{left:24px;}
        .nav-btn.right{right:24px;}

        .canvas{
          position:relative; margin:22px auto 10px auto;
          width:min(1400px, 92vw);
          height:min(75vh, 900px);
          display:flex; align-items:center; justify-content:center;
          overflow:visible;
          background:#0a0f1d; border:1px solid #18243a; border-radius:12px;
        }
        .canvas img{
          width:100%;
          height:100%;
          object-fit:contain;
          image-orientation: from-image;
          transition:transform .15s ease;
          transform-origin:center center;
          pointer-events:auto;
        }
        .canvas .empty{color:#a9b8cf; font-size:14px;}

        /* ✨ CSS BARU UNTUK PANORAMA PLACEHOLDER */
        .panorama-canvas {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            gap: 20px;
        }
        .panorama-overlay {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(15, 22, 39, 0.7);
          backdrop-filter: blur(5px);
          padding: 10px 15px;
          border-radius: 8px;
          text-align: center;
          color: #e9f1fb;
          font-size: 14px;
        }
        .panorama-annotation-anchor{
          width:auto !important;
          height:auto !important;
          overflow:visible !important;
          cursor:pointer;
        }
        .panorama-annotation-hotspot{
          position:relative;
          display:flex;
          align-items:center;
          justify-content:center;
          width:16px;
          height:16px;
          border-radius:999px;
          background:var(--annotation-color, #22c55e);
          border:2px solid rgba(255,255,255,.95);
          box-shadow:0 0 0 4px rgba(15, 22, 39, .35), 0 8px 20px rgba(0,0,0,.35);
        }
        .panorama-annotation-bubble{
          position:absolute;
          left:50%;
          bottom:22px;
          transform:translate(-50%, 8px) scale(.98);
          min-width:140px;
          max-width:260px;
          padding:8px 10px;
          border-radius:10px;
          background:rgba(10,15,29,.96);
          border:1px solid #243657;
          color:#e9f1fb;
          font-size:12px;
          line-height:1.35;
          white-space:normal;
          opacity:0;
          visibility:hidden;
          transition:opacity .15s ease, transform .15s ease, visibility .15s ease;
          pointer-events:none;
          text-align:left;
        }
        .panorama-annotation-bubble::after{
          content:"";
          position:absolute;
          left:50%;
          bottom:-6px;
          transform:translateX(-50%);
          border:6px solid transparent;
          border-top-color:rgba(10,15,29,.96);
        }
        .panorama-annotation-anchor:hover .panorama-annotation-bubble,
        .panorama-annotation-anchor:focus-visible .panorama-annotation-bubble{
          opacity:1;
          visibility:visible;
          transform:translate(-50%, 0) scale(1);
        }
        /* Akhir dari CSS baru */

        /* ===== Timeline styles (3 rows) ===== */
        .timeline{
          position:relative; width:min(1400px, 92vw); margin:8px auto 16px auto;
          display:flex; flex-direction:column; gap:8px;
        }
        .timeline .current-time{
          text-align:center; font-size:13px; color:#8ee6ff; font-weight:700;
        }
        .timeline .track{ position:relative; height:24px; }
        .timeline .rail{
          position:absolute; left:0; right:0; top:50%; height:2px; transform:translateY(-50%);
          background:#435577; border-radius:2px;
        }
        .timeline .tick{
          position:absolute; top:50%; transform:translate(-50%, -50%);
          width:12px; height:12px; border-radius:50%;
          border:2px solid #253552; background:#4c6fae; cursor:pointer;
          box-shadow:0 0 0 0 rgba(0,0,0,0);
        }
        .timeline .tick.active{
          width:16px; height:16px; background:#6ee7e7; border-color:#15404f;
          box-shadow:0 0 0 4px rgba(110,231,231,0.12);
        }
        .timeline .tick-labels{
          position:relative; height:32px;
        }
        .timeline .tick-label{
          position:absolute; top:0; transform:translateX(-50%);
          text-align:center; min-width:40px;
          color:#a9b8cf; font-size:11px; line-height:1.15;
          pointer-events:none;
        }
        .timeline .tick-label.active .date{
          color:#8ee6ff; font-weight:700;
        }
        .timeline .tick-label .ann-group{
          display:flex; gap:3px; justify-content:center; margin-top:2px; min-height:10px;
        }
        .timeline .tick-label .ann-dot{
          width:6px; height:6px; border-radius:50%;
          /* color set inline */
        }
        .timeline .tick-label .ann-more{
          font-size:10px; color:#cde8ff; margin-left:2px;
        }

        /* Side panel */
        .side{background:#0f1627; border-left:1px solid #ffffff1a; display:flex; flex-direction:column; min-width:0;}
        .side.closed{display:none;}
        .side-head{padding:12px 12px 8px 12px; border-bottom:1px solid #ffffff16;}
        .side-head h4{margin:0; font-size:16px;}
        .side-list{padding:10px; display:flex; flex-direction:column; gap:8px; overflow:auto;}
        .side-empty{color:#a9b8cf; font-size:13px; padding:6px;}
        .card{
          display:flex; gap:8px; align-items:flex-start; background:#101a2d; border:1px solid #203355; border-radius:10px; padding:10px;
        }
        .card .dot{width:12px; height:12px; border-radius:50%;}
        .card .body{flex:1; color:#dbe5f7; font-size:13px;}
        .card .actions{display:flex; gap:6px;}
        .card .edit, .card .delete{ background:transparent; border:0; color:#a9b8cf; cursor:pointer; }
        .card .delete{ color:#ff7d7d; }

        /* Notes UI */
        .note{
          position:absolute; width:14px; height:14px; border-radius:50%; transform:translate(-50%,-50%);
          box-shadow:0 2px 10px rgba(0,0,0,.35); z-index:30; cursor:pointer;
        }
        .note-pop{
          position:absolute; left:16px; top:-6px; transform:translateY(-100%);
          max-width:320px; padding:8px 10px; border-radius:10px;
          background:#0f172a; border:1px solid #324b77; color:#e7f0ff;
          box-shadow:0 10px 28px rgba(0,0,0,.45); font-size:13px; line-height:1.25;
          opacity:0; pointer-events:none; transition:opacity .12s ease;
          white-space:normal;
        }
        .note:hover .note-pop{opacity:1;}
        .note-toolbar{
          position:absolute; left:16px; top:10px;
          display:flex; gap:6px; background:#1b263c; border:1px solid #304569; border-radius:8px;
          padding:4px; z-index:42; box-shadow:0 10px 24px rgba(0,0,0,.5);
        }
        .note-toolbar .icon{
          background:#243657; border:1px solid #35507c; border-radius:6px; padding:4px 6px; color:#e9f1fb; cursor:pointer;
        }
        .note-toolbar .icon.danger{background:#522e2e; border-color:#7a4b4b;}
        .note-editor{
          position:absolute; left:16px; top:16px; width:360px;
          background:#1b263c; border:1px solid #304569; border-radius:12px; padding:10px;
          color:#e9f1fb; box-shadow:0 18px 36px rgba(0,0,0,.6); z-index:40;
        }
        .note-editor textarea{
          width:100%; min-height:96px; resize:vertical; background:#122039; color:#e9f1fb;
          border:1px solid #27406b; border-radius:8px; padding:8px;
        }
        .note-editor .row{display:flex; align-items:center; gap:8px; margin-top:8px;}
        .note-editor .dot{width:18px; height:18px; border-radius:50%; border:1px solid #ffffff44; cursor:pointer;}
        .note-editor .spacer{flex:1;}
        .note-editor .tiny{
          background:#2a3b59; color:#e9f1fb; border:1px solid #3b5485; border-radius:8px; padding:6px 10px;
        }
        .note-editor .tiny.danger{
          background:#522e2e; border-color:#7a4b4b; color:#ffdcdc;
        }
        .note-editor .primary{background:#2b4c7e; border:1px solid #3e67a6; color:#fff; border-radius:8px; padding:6px 10px;}

        /* --- STYLES BARU UNTUK CompareSlider --- */
        .canvas.compare {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          justify-content: flex-start;
          gap: 10px;
          min-height: calc(100vh - 140px); /* Menyesuaikan dengan tinggi layar */
        }
        .compare-wrap {
          position: relative;
          flex: 1; /* Biar flexbox-nya ngisi ruang */
          height: auto;
          overflow: hidden;
          background: #0a0f1d;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          user-select: none;
          -webkit-user-drag: none;
          touch-action: none;
          cursor: ew-resize;
        }
        .compare-wrap.dragging { cursor: col-resize; }

        .cmp-img {
          position: absolute; inset: 0;
          width: 100%; height: 100%;
          object-fit: contain;
          user-select: none;
          pointer-events: none;
          will-change: clip-path, transform;
          transform: translateZ(0);
        }
        .cmp-img.base { z-index: 1; }
        .cmp-img.top { z-index: 3; }

        .cmp-ann-layer {
          position: absolute;
          inset: 0;
          display: block;
          pointer-events: none;
        }
        .cmp-ann-layer.base { z-index: 2; }
        .cmp-ann-layer.top { z-index: 4; }

        .cmp-note {
          position: absolute;
          transform: translate(-50%, -50%);
          width: 12px; height: 12px;
          border-radius: 9999px;
          opacity: .95;
          pointer-events: auto;
        }

        .cmp-note-pop {
          position: absolute;
          left: 50%;
          bottom: 14px;
          transform: translate(-50%, 8px) scale(.98);
          background: rgba(10,15,29,.96);
          color: #e9f1fb;
          border: 1px solid #243657;
          border-radius: 8px;
          padding: 6px 8px;
          font-size: 12px;
          line-height: 1.25;
          max-width: 240px;
          white-space: normal;
          box-shadow: 0 6px 18px rgba(0,0,0,.35);
          opacity: 0;
          visibility: hidden;
          transition: opacity .15s ease, transform .15s ease, visibility .15s ease;
          pointer-events: none;
          z-index: 9;
        }
        .cmp-note-pop::after {
          content: "";
          position: absolute;
          left: 50%;
          bottom: -6px;
          transform: translateX(-50%);
          border: 6px solid transparent;
          border-top-color: rgba(10,15,29,.96);
        }
        .cmp-note:hover .cmp-note-pop,
        .cmp-note:focus-visible .cmp-note-pop {
          opacity: 1;
          visibility: visible;
          transform: translate(-50%, 0) scale(1);
        }

        .split-line {
          position: absolute; top: 0; bottom: 0; width: 2px;
          background: #67a2ff55;
          transform: translateX(-1px);
          z-index: 7;
          pointer-events: none;
        }
        .split-handle {
          position: absolute; top: 50%;
          transform: translate(-50%, -50%);
          width: 34px; height: 34px; border-radius: 999px;
          display: grid; place-items: center;
          background: #121b2e; color: #e9f1fb; border: 1px solid #243657;
          box-shadow: 0 6px 18px rgba(0,0,0,.35);
          z-index: 8;
          pointer-events: none;
        }

        .cmp-slider-row {
          margin: 0 8px;
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .cmp-slider-row input[type="range"] {
          width: 100%;
          accent-color: #6ee7e7;
        }
        .cmp-slider-label {
          text-align: left;
          font-size: 12px;
          color: #a9b8cf;
          margin-bottom: 6px;
        }
        .cmp-pickers {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          padding: 0 8px 8px 8px;
        }
        .picker .label {
          font-size: 12px;
          color: #a9b8cf;
          margin-bottom: 6px;
        }
        .picker select {
          width: 100%;
          background: #101a2d;
          color: #e9f1fb;
          border: 1px solid #203355;
          border-radius: 10px;
          padding: 10px;
        }
        /* Akhir dari CSS baru */
      `}</style>
    </div>,
    document.body
  );
}

// ✨ ICON BARU UNTUK FOLDER KAMERA
const createFolderIcon = (
  folderName,
  photoCount,
  projectColor = "#0ea5b0",
  annotations = { count: 0, colors: [] }
) => {
  const esc = (s) =>
    String(s).replace(
      /[<>&]/g,
      (m) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[m])
    );

  const annotationDots = (annotations.colors || [])
    .slice(0, 3) // Batasi maksimal 3 titik agar tidak terlalu ramai
    .map(
      (color) =>
        `<span class="ann-dot-label" style="background-color: ${color}"></span>`
    )
    .join("");

  const html = `
    <div class="folder-marker-wrapper">
      <div class="folder-marker-icon" style="background-color: ${projectColor};">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="white" viewBox="0 0 16 16">
          <path d="M10.5 8.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0z"/>
          <path d="M2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4H2zm.5 2a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm9 2.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0z"/>
        </svg>
        <div class="folder-marker-badge">${photoCount}</div>
      </div>
      <div class="folder-marker-label">
        <div class="ann-dots-container">${annotationDots}</div>
        ${esc(folderName)}
      </div>
    </div>`;

  return L.divIcon({
    html,
    className: "folder-marker-container",
    iconSize: [120, 60],
    iconAnchor: [60, 40],
  });
};

// ✨ ICON BARU UNTUK LOKASI PANORAMA
const createPanoramaIcon = (
  locationName,
  photoCount,
  projectColor = "#FAB12F" // Warna biru sebagai default untuk panorama
) => {
  const esc = (s) =>
    String(s).replace(
      /[<>&]/g,
      (m) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[m])
    );

  const html = `
    <div class="folder-marker-wrapper">
      <div class="folder-marker-icon" style="background-color: ${projectColor};">
    
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="white" viewBox="0 0 16 16">
          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
        </svg>
        <div class="folder-marker-badge">${photoCount}</div>
      </div>
      <div class="folder-marker-label">${esc(locationName)}</div>
    </div>`;

  return L.divIcon({
    html,
    className: "folder-marker-container", // Kita bisa pakai class yang sama
    iconSize: [120, 60],
    iconAnchor: [60, 40],
  });
};

/* ---------- Main Projects Map ---------- */
export default function Project() {
  const { layersRefreshKey } = useOutletContext();
  const {
    dataLayers: allDataLayers,
    activeLayerIds,
    photoRefreshKey,
    folders,
    panoramas,
    mapFocusTarget,
    clearMapFocusTarget,
  } = useOutletContext();
  const token = localStorage.getItem("token") || "";
  const [projects, setProjects] = useState([]);
  const [photosByProject, setPhotosByProject] = useState({});
  const [dataLayers, setDataLayers] = useState({});
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);
  const [isLoadingLayers, setIsLoadingLayers] = useState(false);
  const [base, setBase] = useState("sat");
  const [err, setErr] = useState("");
  const { search } = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [visibleLayers, setVisibleLayers] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentDisplayMonth, setCurrentDisplayMonth] = useState(new Date());
  const [annotationMeta, setAnnotationMeta] = useState({});
  const [annotationRefreshKey, setAnnotationRefreshKey] = useState(0);
  //const [folders, setFolders] = useState({});
  //const [foldersLoading, setFoldersLoading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const navigate = useNavigate();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const urlProjectId = params.get("projectId");
  const urlFolder = params.get("folder");
  const urlPanoramaId = params.get("panoramaId");
  const urlPhotoId = params.get("photoId");
  const urlViewerMode = params.get("mode") || "photo";
  const activeProjectId = useMemo(
    () => params.get("projectId") || null,
    [search]
  );
  const [panoramaPhotos, setPanoramaPhotos] = useState({});
  const [isLoadingPanoPhotos, setIsLoadingPanoPhotos] = useState(false);

  // ** START: KODE YANG DIPERBAIKI **
  // 1. Definisikan fungsi fetch foto dengan useCallback agar tidak dibuat ulang
  const fetchPhotosForProject = useCallback(
    async (projectId) => {
      if (!projectId) return;
      setIsLoadingPhotos(true);
      setErr("");
      try {
        const res = await fetch(`${API_BASE}/projects/${projectId}/photos`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!res.ok)
          throw new Error(`Failed to load photos for project ${projectId}`);
        const photos = await res.json();
        setPhotosByProject((prev) => ({
          ...prev,
          [projectId]: Array.isArray(photos) ? photos : [],
        }));
      } catch (e) {
        setErr(e.message);
      } finally {
        setIsLoadingPhotos(false);
      }
    },
    [token]
  );
  // 2. Panggil fungsi fetch foto dari satu useEffect yang terpusat
  // useEffect ini akan berjalan saat activeProjectId berubah ATAU saat photoRefreshKey berubah
  useEffect(() => {
    if (activeProjectId) {
      fetchPhotosForProject(activeProjectId);
    }
  }, [activeProjectId, photoRefreshKey, fetchPhotosForProject]);

  // ** END: KODE YANG DIPERBAIKI **

  // useEffect baru untuk membuka viewer
  useEffect(() => {
    if (!urlProjectId || !urlPhotoId) return;

    const project = projects.find((p) => p.id.toString() === urlProjectId);
    if (!project) return;

    if (urlViewerMode === "panorama" && urlPanoramaId) {
      const panoLocation = (panoramas[urlProjectId] || []).find(
        (p) => p.id.toString() === urlPanoramaId
      );
      const projectPanoramaPhotos = panoramaPhotos[urlProjectId];
      if (!panoLocation || !projectPanoramaPhotos) return;

      const locationPhotos = projectPanoramaPhotos.filter(
        (photo) =>
          photo.panorama_location_id?.toString() === urlPanoramaId ||
          photo.photo_folder === panoLocation.location_name
      );
      const initialPhotoIndex = locationPhotos.findIndex(
        (photo) => photo.id.toString() === urlPhotoId
      );

      if (locationPhotos.length > 0 && initialPhotoIndex !== -1) {
        setViewer({
          folder: panoLocation.location_name,
          photos: locationPhotos,
          index: initialPhotoIndex,
          projectName: project.name,
          mode: "panorama",
        });
      } else {
        navigate(`/Project?projectId=${urlProjectId}`, { replace: true });
      }
      return;
    }

    if (!urlFolder) return;
    const photos = photosByProject[urlProjectId];
    if (!photos) return;

    const folderPhotos = photos.filter(
      (p) => (p.photo_folder || "(Uncategorized)").trim() === urlFolder
    );
    const initialPhotoIndex = folderPhotos.findIndex(
      (p) => p.id.toString() === urlPhotoId
    );

    if (folderPhotos.length > 0 && initialPhotoIndex !== -1) {
      setViewer({
        folder: urlFolder,
        photos: folderPhotos,
        index: initialPhotoIndex,
        projectName: project.name,
        mode: "photo",
      });
    } else {
      navigate(`/Project?projectId=${urlProjectId}`, { replace: true });
    }
  }, [
    urlProjectId,
    urlFolder,
    urlPanoramaId,
    urlPhotoId,
    urlViewerMode,
    projects,
    photosByProject,
    panoramas,
    panoramaPhotos,
    navigate,
  ]);

  // 4. Fetch data layers saat proyek aktif berubah ATAU ketika ada perubahan dari sidebar
  useEffect(() => {
    const fetchLayersForProject = async (projectId) => {
      setIsLoadingLayers(true);
      try {
        const res = await fetch(`${API_BASE}/projects/${projectId}/layers`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!res.ok)
          throw new Error(`Failed to load layers for project ${projectId}`);
        const layers = await res.json();

        const layerData = await Promise.all(
          layers.map(async (layer) => {
            try {
              const fileRes = await fetch(`${API_BASE}${layer.file_url}`);
              if (!fileRes.ok)
                throw new Error(
                  `Failed to fetch GeoJSON file for layer ${layer.id}`
                );
              const geojson = await fileRes.json();
              return { ...layer, geojson };
            } catch (e) {
              console.error(`Error loading GeoJSON for layer ${layer.id}:`, e);
              return { ...layer, geojson: null };
            }
          })
        );

        setDataLayers((prev) => ({
          ...prev,
          [projectId]: layerData.filter((l) => l.geojson !== null),
        }));
      } catch (e) {
        setErr(e.message);
      } finally {
        setIsLoadingLayers(false);
      }
    };

    if (activeProjectId) {
      fetchLayersForProject(activeProjectId);
    }
  }, [activeProjectId, token, layersRefreshKey]);

  // Tambahkan useEffect baru ini
  useEffect(() => {
    if (activeProjectId && photosByProject[activeProjectId]?.length > 0) {
      const photos = photosByProject[activeProjectId];
      // Urutkan untuk mendapatkan foto terbaru
      const sortedPhotos = [...photos].sort(
        (a, b) =>
          new Date(b.taken_at || b.created_at) -
          new Date(a.taken_at || a.created_at)
      );
      const latestDate = new Date(
        sortedPhotos[0].taken_at || sortedPhotos[0].created_at
      );
      setSelectedDate(latestDate);
      setCurrentDisplayMonth(latestDate);
    } else {
      // Reset saat tidak ada proyek aktif
      setSelectedDate(null);
    }
  }, [activeProjectId, photosByProject]);

  // 5. Fetch SEMUA anotasi lalu filter berdasarkan proyek yang aktif
  useEffect(() => {
    const fetchAnnotationMeta = async (projectId, projectName) => {
      // Hindari fetch ulang jika data sudah ada
      if (annotationMeta[projectId]) return;
      try {
        const res = await fetch(`${API_BASE}/photo_annotation`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!res.ok) throw new Error(`Failed to load annotations`);
        const allAnnotations = await res.json();
        const projectAnnotationsData = Array.isArray(allAnnotations)
          ? allAnnotations.filter((ann) => ann.project_name === projectName)
          : [];
        const metaByFile = projectAnnotationsData.reduce((acc, ann) => {
          const url = ann.file_url;
          if (!acc[url]) {
            acc[url] = { count: 0, colors: new Set() };
          }
          acc[url].count += 1;
          if (ann.color) {
            acc[url].colors.add(ann.color);
          }
          return acc;
        }, {});
        Object.keys(metaByFile).forEach((key) => {
          metaByFile[key].colors = [...metaByFile[key].colors];
        });
        setAnnotationMeta((prev) => ({ ...prev, [projectId]: metaByFile }));
      } catch (e) {
        console.error(e.message || "Failed to load annotation metadata");
        setAnnotationMeta((prev) => ({ ...prev, [projectId]: {} }));
      }
    };
    if (activeProjectId && photosByProject[activeProjectId]) {
      const projectInfo = projects.find(
        (p) => p.id.toString() === activeProjectId
      );
      if (projectInfo) {
        fetchAnnotationMeta(activeProjectId, projectInfo.name);
      }
    }
  }, [
    activeProjectId,
    photosByProject,
    projects,
    token,
    annotationMeta,
    annotationRefreshKey,
  ]);

  // Definisikan fungsi refresh yang dapat diteruskan ke ViewerModal
  const refreshAnnotationMeta = useCallback(() => {
    if (activeProjectId) {
      setAnnotationMeta((prev) => {
        const { [activeProjectId]: _, ...rest } = prev;
        return rest;
      });
    }
    setAnnotationRefreshKey((prev) => prev + 1);
  }, [activeProjectId]);

  useEffect(() => {
    const fetchPanoramaPhotosForProject = async (projectId) => {
      if (!projectId) return;
      setIsLoadingPanoPhotos(true);
      try {
        // Usamos un endpoint que filtra por project_id. Si no existe, necesitarás
        // ajustarlo en el backend o filtrar en el frontend.
        const res = await fetch(
          `${API_BASE}/panorama_photos?project_id=${projectId}`,
          {
            headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          }
        );
        if (!res.ok)
          throw new Error(
            `Failed to load panorama photos for project ${projectId}`
          );
        const photos = await res.json();
        const filteredPhotos = Array.isArray(photos)
          ? photos.filter((photo) => photo.project_id?.toString() === projectId.toString())
          : [];
        setPanoramaPhotos((prev) => ({
          ...prev,
          [projectId]: filteredPhotos,
        }));
      } catch (e) {
        setErr((prev) => prev + " " + e.message); // Concatena errores si es necesario
      } finally {
        setIsLoadingPanoPhotos(false);
      }
    };

    if (activeProjectId) {
      fetchPanoramaPhotosForProject(activeProjectId);
    }
    // Se ejecutará cuando cambie el proyecto activo.
  }, [activeProjectId, token, photoRefreshKey]);

  // 5. ✨ URUTAN DIPERBAIKI: Definisikan projectColors TERLEBIH DAHULU
  const projectColors = useMemo(() => {
    const colors = [
      "#FF0066",
      "#640D5F",
      "#9112BC",
      "#FF9A00",
      "#0D1164",
      "#26667F",
      "#08CB00",
      "#B13BFF",
      "#6C946F",
      "#009FBD",
    ];
    return projects.reduce((acc, project, index) => {
      acc[project.id] = colors[index % colors.length];
      return acc;
    }, {});
  }, [projects]);

  // REEMPLAZA el useMemo de `activeProjectFolders` con este nuevo `allMappableItems`
  const allMappableItems = useMemo(() => {
    if (!activeProjectId) return [];

    const projectInfo = projects.find(
      (p) => p.id.toString() === activeProjectId
    );
    if (!projectInfo) return [];

    const projectColor = projectColors[activeProjectId] || "#0ea5b0";
    const activeProjectPhotos = photosByProject[activeProjectId] || [];
    const activePanoPhotos = panoramaPhotos[activeProjectId] || [];

    // 1. Procesa los folders normales
    const regularFolders = (folders[activeProjectId] || [])
      .map((folder) => {
        const photosInFolder = activeProjectPhotos.filter(
          (p) => (p.photo_folder || "").trim() === folder.folder_name
        );
        return {
          id: `folder-${folder.id}`,
          type: "folder", // <-- Tipo para diferenciar
          name: folder.folder_name,
          lat: toNum(folder.lat),
          lon: toNum(folder.lon),
          photoCount: photosInFolder.length,
          photos: photosInFolder,
          projectName: projectInfo.name,
          projectColor: projectColor,
        };
      })
      .filter((item) => item.lat != null && item.lon != null);

    // 2. Procesa los folders de panorama
    const panoramaFolders = (panoramas[activeProjectId] || [])
      .map((pano) => {
        const photosInPano = activePanoPhotos.filter(
          (p) =>
            p.panorama_location_id?.toString() === pano.id.toString() ||
            p.photo_folder === pano.location_name
        );
        return {
          id: `pano-${pano.id}`,
          type: "panorama", // <-- Tipo para diferenciar
          name: pano.location_name,
          lat: toNum(pano.lat),
          lon: toNum(pano.lon),
          photoCount: photosInPano.length,
          photos: photosInPano,
          projectName: projectInfo.name,
          projectColor: "#FA812F", // Un color azul distintivo para panoramas
        };
      })
      .filter((item) => item.lat != null && item.lon != null);

    // 3. Devuelve el array combinado
    return [...regularFolders, ...panoramaFolders];
  }, [
    activeProjectId,
    folders,
    panoramas, // <-- Nueva dependencia
    photosByProject,
    panoramaPhotos, // <-- Nueva dependencia
    projects,
    projectColors,
  ]);

const filteredMappableItemsForMap = useMemo(() => {
  const projectAnnotations = annotationMeta[activeProjectId] || {};

  const getFolderAnnotations = (photoList) => {
    let count = 0;
    const colors = new Set();
    photoList.forEach((p) => {
      const meta = projectAnnotations[p.file_url];
      if (meta) {
        count += meta.count;
        meta.colors.forEach((c) => colors.add(c));
      }
    });
    return { count, colors: [...colors] };
  };

  // ✅ TAMBAHKAN INI: Saat tidak ada filter tanggal, tambahkan annotations ke semua item
  if (!selectedDate) {
    return allMappableItems.map(item => ({
      ...item,
      annotations: getFolderAnnotations(item.photos) // ✅ TAMBAHKAN BARIS INI
    }));
  }

  const targetDate = getDayDate(selectedDate);
  const targetTime = targetDate.getTime();

  return allMappableItems
    .map((item) => {
      const photosForDay = item.photos.filter((photo) => {
        const photoDate = getDayDate(photo.taken_at || photo.created_at);
        return photoDate && photoDate.getTime() === targetTime;
      });

      if (photosForDay.length > 0) {
        return {
          ...item,
          photos: photosForDay,
          photoCount: photosForDay.length,
          annotations: getFolderAnnotations(photosForDay), // ✅ PASTIKAN INI AKTIF
        };
      }
      return null;
    })
    .filter(Boolean);
}, [allMappableItems, selectedDate, annotationMeta, activeProjectId]);

  const MapViewManager = () => {
    const map = useMap();
    const lastActiveProjectId = useRef(null);
    const lastActiveFolderCount = useRef(0);
    const lastProjectCount = useRef(0);

    useEffect(() => {
      if (activeProjectId && allMappableItems.length > 0) {
        if (
          activeProjectId !== lastActiveProjectId.current ||
          allMappableItems.length !== lastActiveFolderCount.current
        ) {
          const folderLocations = allMappableItems.map((f) => [f.lat, f.lon]);
          if (folderLocations.length > 0) {
            const bounds = L.latLngBounds(folderLocations);
            map.fitBounds(bounds, { padding: [70, 70], maxZoom: 17 });
            lastActiveProjectId.current = activeProjectId;
            lastActiveFolderCount.current = allMappableItems.length;
          }
        }
      } else if (!activeProjectId && projects.length > 0) {
        if (
          activeProjectId !== lastActiveProjectId.current ||
          projects.length !== lastProjectCount.current
        ) {
          const projectsWithCoords = projects.filter((p) => hasLL(p));
          if (projectsWithCoords.length > 0) {
            const bounds = L.latLngBounds(
              projectsWithCoords.map((p) => [toNum(p.lat), toNum(p.lon)])
            );
            map.fitBounds(bounds, { padding: [50, 50] });
            lastActiveProjectId.current = activeProjectId;
            lastProjectCount.current = projects.length;
          }
        }
      }
    }, [activeProjectId, allMappableItems, projects, map]);

    return null;
  };

  const annotationsByDate = useMemo(() => {
    if (
      !activeProjectId ||
      !photosByProject[activeProjectId] ||
      !annotationMeta[activeProjectId]
    ) {
      return {};
    }

    const photos = photosByProject[activeProjectId];
    const projectAnnotations = annotationMeta[activeProjectId];
    const byDate = {};

    photos.forEach((photo) => {
      const photoAnnots = projectAnnotations[photo.file_url];
      if (photoAnnots && photoAnnots.count > 0) {
        const dateStr = getDayDate(photo.taken_at || photo.created_at)
          .toISOString()
          .split("T")[0];
        if (!byDate[dateStr]) {
          byDate[dateStr] = { count: 0, colors: new Set() };
        }
        byDate[dateStr].count += photoAnnots.count;
        photoAnnots.colors.forEach((color) =>
          byDate[dateStr].colors.add(color)
        );
      }
    });

    Object.keys(byDate).forEach((key) => {
      byDate[key].colors = [...byDate[key].colors];
    });

    return byDate;
  }, [activeProjectId, photosByProject, annotationMeta]);

  const [layersOpen, setLayersOpen] = useState(false);
  const layerRef = useRef(null);
  const mapRef = useRef(null);
  const mapStateBeforeViewer = useRef(null);
  const handledMapFocusRef = useRef(null);

  useEffect(() => {
    const saved = localStorage.getItem("mapBase");
    if (["dark", "light", "sat"].includes(saved)) setBase(saved);
  }, []);
  useEffect(() => localStorage.setItem("mapBase", base), [base]);
  useEffect(() => {
    const onDocClick = (e) => {
      if (layerRef.current && !layerRef.current.contains(e.target))
        setLayersOpen(false);
    };
    const onEsc = (e) => e.key === "Escape" && setLayersOpen(false);
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    if (!mapFocusTarget || !mapRef.current) return;
    if (handledMapFocusRef.current === mapFocusTarget.requestId) return;
    if (!activeProjectId || String(mapFocusTarget.projectId) !== String(activeProjectId)) {
      return;
    }
    if (allMappableItems.length === 0) return;

    handledMapFocusRef.current = mapFocusTarget.requestId;
    const frame = requestAnimationFrame(() => {
      if (!mapRef.current) return;

      const boundsPoints = (mapFocusTarget.bounds || [])
        .map((point) => [toNum(point?.lat), toNum(point?.lon)])
        .filter(([lat, lon]) => lat != null && lon != null);

      if (boundsPoints.length > 1) {
        mapRef.current.fitBounds(L.latLngBounds(boundsPoints), {
          padding: [70, 70],
          maxZoom: 18,
          animate: true,
        });
        clearMapFocusTarget?.();
        return;
      }

      const singlePoint = boundsPoints[0];
      const lat = singlePoint?.[0] ?? toNum(mapFocusTarget.lat);
      const lon = singlePoint?.[1] ?? toNum(mapFocusTarget.lon);
      if (lat == null || lon == null) {
        clearMapFocusTarget?.();
        return;
      }

      const targetZoom = Math.max(mapRef.current.getZoom(), mapFocusTarget.zoom ?? 18);
      mapRef.current.flyTo([lat, lon], targetZoom, {
        animate: true,
        duration: 1.2,
      });
      clearMapFocusTarget?.();
    });

    return () => cancelAnimationFrame(frame);
  }, [
    mapFocusTarget,
    activeProjectId,
    allMappableItems.length,
    clearMapFocusTarget,
  ]);

  const handleSearch = useCallback(
    async (term) => {
      if (!term.trim()) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      try {
        const res = await fetch(`${API_BASE}/projects/`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (!res.ok) throw new Error("Failed to fetch projects");
        const allProjects = await res.json();
        const filtered = allProjects.filter(
          (project) =>
            project.name.toLowerCase().includes(term.toLowerCase()) ||
            project.location_name?.toLowerCase().includes(term.toLowerCase())
        );
        setSearchResults(filtered);
        setShowSearchResults(true);
      } catch (error) {
        setErr("Search failed: " + error.message);
      }
    },
    [token]
  );

  // const loadProjects = useCallback(async () => {
  //   try {
  //     const res = await fetch(`${API_BASE}/projects/`, {
  //       headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  //     });
  //     if (!res.ok) throw new Error(`Failed to load projects (${res.status})`);
  //     const data = await res.json();
  //     const list = Array.isArray(data) ? data : [];
  //     setProjects(list);
  //   } catch (e) {
  //     setErr(e.message || "Failed to load projects");
  //   }
  // }, [token]);

  // useEffect(() => {
  //   loadProjects();
  // }, [loadProjects]);

  const loadProjectsFiltered = useCallback(async () => {
  try {
    const res = await fetch(`${API_BASE}/projects/`, {
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    });
    if (!res.ok) throw new Error(`Failed to load projects (${res.status})`);
    const data = await res.json();
    
    setProjects(Array.isArray(data) ? data : []);
  } catch (e) {
    setErr(e.message || "Failed to load projects");
  }
}, [token]);

useEffect(() => {
  loadProjectsFiltered();
}, [loadProjectsFiltered]);

  // ICON BARU UNTUK LOKASI PROYEK
  const createProjectIcon = (projectName) => {
    const esc = (s) =>
      String(s).replace(
        /[<>&]/g,
        (m) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;" }[m])
      );
    const html = `
    <div class="project-marker-wrapper">
      <div class="project-location-pin">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16">
          <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z"/>
        </svg>
      </div>
      <div class="project-marker-label">
        ${esc(projectName)}
      </div>
    </div>`;

    return L.divIcon({
      html,
      className: "project-marker-container",
      iconSize: [120, 50],
      iconAnchor: [60, 42],
    });
  };

  const openViewer = useCallback((folderData) => {
    if (mapRef.current) {
      mapStateBeforeViewer.current = {
        center: mapRef.current.getCenter(),
        zoom: mapRef.current.getZoom(),
      };
    }
    setViewer({
      folder: folderData.name,
      photos: folderData.photos,
      index: 0,
      projectName: folderData.projectName,
      // ✨ PASS THE NEW MODE PROP BASED ON `item.type`
      mode: folderData.type === "panorama" ? "panorama" : "photo",
    });
  }, []);

  const closeViewer = () => {
    setViewer(null);
    const url = new URL(window.location.href);
    url.searchParams.delete("folder");
    url.searchParams.delete("panoramaId");
    url.searchParams.delete("photoId");
    url.searchParams.delete("mode");
    navigate({ search: url.search }, { replace: true });
    if (mapRef.current && mapStateBeforeViewer.current) {
      const { center, zoom } = mapStateBeforeViewer.current;
      mapRef.current.setView(center, zoom, { animate: false });
    }
  };

  const toggleLayerVisibility = (layerId) => {
    setVisibleLayers((prev) => ({
      ...prev,
      [layerId]: !prev[layerId],
    }));
  };

  return (
    <div className="map-fixed" style={{ left: 300 }}>
      <style>{`
        .map-fixed{position:fixed; top:0; right:0; bottom:0; background:#0b1220; z-index:0;}
        .leaflet-container{width:100%; height:100%;}
        .layer-ctl{position:absolute; right:12px; top:12px; z-index:1000; width:200px;}
        .layer-toggle{width:100%; display:flex; align-items:center; gap:8px; justify-content:space-between;
          background:#0f1627; color:#e9f1fb; border:1px solid #ffffff24; border-radius:10px; padding:10px 12px; cursor:pointer;}
        .layer-toggle:hover{background:#141f35;}
        .layer-menu{margin-top:6px; background:#0f1627; border:1px solid #ffffff24; border-radius:10px; padding:6px; box-shadow:0 10px 24px rgba(0,0,0,.35);}
        .layer-btn{width:100%; display:flex; align-items:center; gap:8px; padding:8px 10px; border-radius:8px;
          background:#16233a; color:#e9f1fb; border:1px solid #22324f; cursor:pointer;}
        .layer-btn + .layer-btn{margin-top:6px;}
        .layer-btn:hover{background:#1b2d4a;}
        .layer-btn.active{background:#23324e; border-color:#2d446f; box-shadow:0 0 0 3px #6ea8fe33;}
        .chev{transition:transform .18s ease;}
        .cam-wrap{display:flex; align-items:center; gap:8px;}
        .cam-pin{width:28px; height:28px; border-radius:50%; display:grid; place-items:center; background:#0ea5b0; color:#fff; box-shadow:0 2px 8px rgba(0,0,0,.35);}
        .cam-label{background:#0f172a; color:#e2e8f0; border:1px solid #334155; padding:6px 10px; border-radius:10px; font-weight:700; white-space:nowrap; box-shadow:0 4px 18px rgba(0,0,0,.35);}
        .leaflet-tooltip{background:transparent; border:0; box-shadow:none;}
        @media (max-width: 900px){ .map-fixed{left:0 !important;} }

        .search-container {
            position: absolute;
            top: 12px;
            left: 12px;
            z-index: 1000;
            width: 320px;
        }

        .search-box {
            position: relative;
            display: flex;
            align-items: center;
            background: #0f1627;
            border: 1px solid #ffffff24;
            border-radius: 10px;
            padding: 10px 12px;
            color: #e9f1fb;
        }

        .search-box i {
            margin-right: 8px;
            color: #a9b8cf;
        }

        .search-box input {
            flex-grow: 1;
            background: none;
            border: none;
            outline: none;
            color: #e9f1fb;
            font-size: 14px;
        }

        .search-box input::placeholder {
            color: #a9b8cf;
        }

        .clear-search {
            background: none;
            border: none;
            color: #a9b8cf;
            cursor: pointer;
            transition: color 0.2s;
        }

        .clear-search:hover {
            color: #fff;
        }

        .search-results {
            position: absolute;
            top: 100%;
            left: 0;
            width: 100%;
            background: #0f1627;
            border: 1px solid #ffffff24;
            border-radius: 10px;
            box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
            margin-top: 6px;
            z-index: 999;
            max-height: 300px;
            overflow-y: auto;
        }

        .search-result-item {
            padding: 10px 12px;
            cursor: pointer;
            transition: background 0.2s;
        }

        .search-result-item:hover {
            background: #141f35;
        }

        .project-name {
            font-weight: 600;
            color: #e9f1fb;
        }

        .project-location {
            font-size: 12px;
            color: #a9b8cf;
        }

        .no-results {
            padding: 10px 12px;
            color: #a9b8cf;
            font-style: italic;
        }

        .leaflet-tooltip {
          background: #1b263c !important;
          color: #fff !important;
          border: 1px solid #324b77 !important;
          border-radius: 8px !important;
          font-weight: 600;
          font-size: 13px;
          box-shadow: 0 10px 28px rgba(0,0,0,.45);
          padding: 8px 12px;
        }
      `}</style>
      <div className="search-container">
        <div className="search-box">
          <i className="bi bi-search" />
          <input
            type="text"
            placeholder="Search projects by name..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              handleSearch(e.target.value);
            }}
            onFocus={() => searchTerm && setShowSearchResults(true)}
          />
          {searchTerm && (
            <button
              className="clear-search"
              onClick={() => {
                setSearchTerm("");
                setSearchResults([]);
                setShowSearchResults(false);
              }}
            >
              <i className="bi bi-x" />
            </button>
          )}
        </div>
        {showSearchResults && searchResults.length > 0 && (
          <div className="search-results">
            {searchResults.map((project) => (
              <div
                key={project.id}
                className="search-result-item"
                onClick={() => {
                  setSearchTerm(project.name);
                  setShowSearchResults(false);
                  navigate(`/Project?projectId=${project.id}`);
                }}
              >
                <div className="project-name">{project.name}</div>
                <div className="project-location">{project.location_name}</div>
              </div>
            ))}
          </div>
        )}
        {showSearchResults && searchTerm && searchResults.length === 0 && (
          <div className="search-results">
            <div className="no-results">No projects found</div>
          </div>
        )}
      </div>
      <MapContainer
        center={[20, 0]}
        zoom={2}
        whenCreated={(m) => (mapRef.current = m)}
        zoomControl={true}
        attributionControl={true}
        maxZoom={19}
      >
        {base === "dark" && (
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
            attribution="&copy; OSM & CARTO"
            maxZoom={19}
          />
        )}
        {base === "light" && (
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OSM"
            maxZoom={19}
          />
        )}
        {base === "sat" && (
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Tiles &copy; Esri"
            maxZoom={19}
          />
        )}
        <MapViewManager />
        {!activeProjectId && (
          <>
            {projects
              .filter((p) => hasLL(p))
              .map((project) => (
                <Marker
                  key={`project-${project.id}`}
                  position={[toNum(project.lat), toNum(project.lon)]}
                  icon={createProjectIcon(project.name)}
                  eventHandlers={{
                    click: () => {
                      navigate(`/Project?projectId=${project.id}`);
                    },
                  }}
                >
                  <Tooltip direction="top">
                    <div className="project-tooltip-hover">
                      <h4>{project.name}</h4>
                      <p>{project.location_name}</p>
                    </div>
                  </Tooltip>
                </Marker>
              ))}
          </>
        )}
        {activeProjectId && (
          <>
            {" "}
            {/* ✨ Lakukan iterasi pada variabel baru yang sudah difilter */}
            {filteredMappableItemsForMap.map((item) => (
              <Marker
                key={item.id}
                position={[item.lat, item.lon]} // ✨ LOGIKA KONDISIONAL UNTUK MEMILIH IKON YANG TEPAT
                icon={
                  item.type === "panorama"
                    ? createPanoramaIcon(
                      item.name,
                      item.photoCount,
                      item.projectColor
                    )
                    : createFolderIcon(
                      item.name,
                      item.photoCount,
                      item.projectColor,
                      item.annotations
                    )
                }
                eventHandlers={{
                  click: () => {
                    // Panggil fungsi openViewer yang sudah diperbarui
                    openViewer(item);
                  },
                }}
              />
            ))}
            {" "}
          </>
        )}
        {activeProjectId &&
          (dataLayers[activeProjectId] || []).map((layer) => {
            const isVisible =
              activeLayerIds[layer.id] !== false && !!activeLayerIds[layer.id];
            if (!isVisible || !layer.geojson) return null;
            return (
              <GeoJSON
                key={layer.id}
                data={layer.geojson}
                style={() => ({
                  color: "#FF5733",
                  weight: 3,
                  opacity: 0.7,
                  fillColor: "#FFC300",
                  fillOpacity: 0.2,
                })}
              >
                <Tooltip sticky>{layer.layer_name}</Tooltip>
              </GeoJSON>
            );
          })}
      </MapContainer>
      {activeProjectId && (
        <Timeline
          photos={photosByProject[activeProjectId] || []}
          selectedDate={selectedDate}
          onDateChange={(date) => setSelectedDate(date)}
          annotationsByDate={annotationsByDate}
          currentMonth={currentDisplayMonth}
          onMonthChange={(direction) => {
            setCurrentDisplayMonth((prev) => {
              const newDate = new Date(prev);
              newDate.setMonth(prev.getMonth() + direction);
              return newDate;
            });
          }}
        />
      )}
      {viewer && (
        <ViewerModal
          open
          onClose={closeViewer}
          folder={viewer.folder}
          projectName={viewer.projectName}
          photos={viewer.photos}
          startIndex={viewer.index}
          apiBase={API_BASE}
          onAnnotationSaved={refreshAnnotationMeta}
          // ✨ PASS THE NEW MODE PROP
          mode={viewer.mode}
        />
      )}
      {err && (
        <div
          style={{
            position: "absolute",
            left: 12,
            bottom: 12,
            zIndex: 1100,
            background: "#1f2937",
            color: "#fee2e2",
            border: "1px solid #fca5a5",
            borderRadius: 8,
            padding: "8px 10px",
          }}
        >
          {err}
        </div>
      )}
    </div>
  );
}
